"use client";

import Link from "next/link";
import { ArrowRight, Lock } from "lucide-react";
import type { BlockedClaimCopy } from "@/lib/draft-projects/claim-reasons";

/**
 * The screen a funnel visitor sees when their claim is refused for an
 * ACCOUNT-STATE reason (plan limit, cancelled subscription, or a role that may
 * not start jobs). Terminal until the account changes — never a retry loop.
 *
 * SHARED BY BOTH DOORS ON PURPOSE. The claim endpoint is reachable from
 * `claim-callback-client.tsx` (post-signup) and `merge-draft.tsx` (the
 * signed-in "you have an unfinished draft" prompt), and the two drifting apart
 * is not hypothetical: the first round of this work updated only the callback
 * and left the merge door rendering the raw `project_limit_reached` token, and
 * the second round left only the merge door navigating the tab away from its
 * own retry button. Sharing the whole panel — copy, CTA target behaviour and
 * the retry affordance together — is what makes that class of divergence
 * impossible rather than merely fixed twice.
 *
 * The account-change CTA opens in a NEW TAB, deliberately. This panel holds the
 * only live retry action for a preserved draft, and nothing in the
 * authenticated app links back to it — Stripe returns a successful checkout to
 * `/billing/success`, which has no resume link. Navigating this tab away would
 * strand the draft at exactly the moment the user has just paid to unstrand it.
 */
export function ClaimBlockedPanel({
  copy,
  onRetry,
}: {
  copy: BlockedClaimCopy;
  onRetry: () => void;
}) {
  return (
    <section className="rounded-lg border border-border bg-surface p-5 text-center sm:p-8">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-tint text-brand-700">
        <Lock className="h-6 w-6" aria-hidden />
      </div>
      <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-brand-700">
        Final step
      </p>
      <h1 className="mt-2 font-heading text-2xl font-extrabold text-navy sm:text-3xl">
        {copy.heading}
      </h1>
      <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-muted">
        {copy.body}
      </p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
        <Link
          href={copy.ctaHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-accent px-5 py-3 text-sm font-bold text-white hover:bg-accent-dark"
        >
          {copy.cta}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-border px-5 py-3 text-sm font-bold text-navy hover:bg-surface-muted"
        >
          Try again — open my job
        </button>
      </div>
      <p className="mt-4 text-xs text-muted">
        <Link href="/dashboard" className="underline hover:no-underline">
          Go to my dashboard
        </Link>
      </p>
    </section>
  );
}
